import { borderColors } from '@/constants/job-pipeline';
import { createSlice } from '@reduxjs/toolkit';
import { ATS_MILESTONES } from '@/constants/ui-library-constants';

const reducerName = 'pipelineData';
const initialState = {
  sort: {
    id: 'updated_at',
    label: 'Last activity (New to old)',
    element: 'updated_at',
    order: 'desc',
  },
  kanbanInitialList: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  kanbanData: {
    data: [],
  },
  rejectedCount: {
    isLoading: true,
    data: {},
    status: undefined,
    error: undefined,
  },
  appReviewSort: {}, // Stage-specific sort state: { [stageId]: { label, value, element, order } }
  needsCustomSortAndAppReviewHasApplications: false,
};

const { reducer, actions: pipelineActions } = createSlice({
  name: reducerName,
  initialState,

  reducers: {
    applySort: (state, { payload }) => {
      state.sort = payload;
      // Reset all stage-specific sorts when global sort changes
      state.appReviewSort = {};
    },

    applyAppReviewSort: (state, { payload }) => {
      const { stageId, sortOption } = payload;
      state.appReviewSort = {
        ...state.appReviewSort,
        [stageId]: sortOption,
      };
    },

    resetAppReviewSort: (state, { payload } = {}) => {
      const { stageId } = payload || {};
      if (stageId) {
        const { [stageId]: _, ...rest } = state.appReviewSort;
        state.appReviewSort = rest;
      } else {
        // Reset all if no specific stage provided
        state.appReviewSort = {};
      }
    },

    increaseRejectCount: (state) => {
      state.rejectedCount.data.total_applications =
        +state?.rejectedCount?.data?.total_applications + 1;
    },

    moveApplication: (state, { payload }) => {
      const { fromStage, toStage } = payload;
      const kanbanData = [...state.kanbanData];

      const fromStageData = kanbanData.find((stage) => stage.id === fromStage);
      const toStageData = kanbanData.find((stage) => stage.id === toStage);

      if (!fromStageData || !toStageData) return;

      const applicationsToMove = [...(fromStageData.applications || [])];

      const fromStageNewCount = 0;
      const toStageNewCount =
        (toStageData.applications_count || 0) +
        (fromStageData.applications_count || 0);

      const newKanbanData = kanbanData.map((stage) => {
        if (stage.id === fromStage) {
          return {
            ...stage,
            applications: [],
            applications_count: fromStageNewCount,
          };
        } else if (stage.id === toStage) {
          return {
            ...stage,
            applications: [
              ...(stage.applications || []),
              ...applicationsToMove,
            ],
            applications_count: toStageNewCount,
          };
        }
        return stage;
      });

      state.kanbanData = newKanbanData;
    },

    removeAllApplication: (state, { payload }) => {
      const { stageId } = payload;
      const kanbanData = [...state.kanbanData];

      const stageData = kanbanData.find((stage) => stage.id === stageId);

      if (!stageData) return;

      const applicationsCount = stageData.applications_count || 0;

      if (state.rejectedCount?.data?.total_applications !== undefined) {
        state.rejectedCount.data.total_applications =
          +state.rejectedCount.data.total_applications + applicationsCount;
      }

      const newKanbanData = kanbanData.map((stage) => {
        if (stage.id === stageId) {
          return {
            ...stage,
            applications: [],
            applications_count: 0,
          };
        }
        return stage;
      });

      state.kanbanData = newKanbanData;
    },

    setKanbanData: (state, { payload }) => {
      const { stages, applicationsPerStage, isAIScoreEnabled, isAiScoringV2 } =
        payload;
      const kanbanData = stages
        ?.map((stage, index) => {
          // Skip populating app review stage with global data if it needs custom sort AND AI scoring is enabled
          const isAppReviewStage =
            stage?.name === 'Application Review' && stage?.mandatory;
          const appReviewSort = state.appReviewSort;
          const globalSort = state.sort;

          // Only consider custom sort if AI scoring V2 is enabled
          const needsCustomSort =
            isAppReviewStage &&
            isAIScoreEnabled &&
            isAiScoringV2 &&
            appReviewSort?.value !== 'page_sort' &&
            (appReviewSort?.element !== globalSort?.element ||
              appReviewSort?.order !== globalSort?.order);

          const currAppPerStage =
            applicationsPerStage?.filter(
              (appPerStage) => appPerStage?.stage_id === stage?.id
            )?.[0] || {};

          if (needsCustomSort && currAppPerStage?.applications?.length > 0) {
            state.needsCustomSortAndAppReviewHasApplications = true;
          }

          // If app review needs custom sort AND AI scoring is enabled, don't populate with global data
          const currAppActions = needsCustomSort
            ? []
            : currAppPerStage?.applications?.map((application) => ({
                ...application,
                name: `${application?.contact?.first_name || ''} ${
                  application?.contact?.last_name || ''
                }`,
              }));

          // Check if we already have custom sorted data for this stage
          // Handle both initial state { data: [] } and direct array state
          const kanbanDataArray = Array.isArray(state.kanbanData)
            ? state.kanbanData
            : state.kanbanData?.data || [];

          const existingStageData = kanbanDataArray?.find(
            (s) => s?.id === stage?.id
          );
          const hasExistingApplications =
            existingStageData?.applications?.length > 0;

          // For stages that need custom sort, preserve existing applications if they exist
          const applicationsToUse =
            needsCustomSort && hasExistingApplications
              ? existingStageData?.applications || [] // Preserve existing custom-sorted applications
              : currAppActions || []; // Use global data for normal stages or when no existing data

          const currAppPerStageWithActions = {
            ...currAppPerStage,
            applications: applicationsToUse,
            // Set loading state for app review when it needs custom sort AND AI scoring is enabled
            // BUT only if we don't already have applications in the stage (to prevent infinite loading)
            isLoading:
              needsCustomSort && !hasExistingApplications
                ? true
                : currAppPerStage?.isLoading || false,
          };

          const colorIndex = index % borderColors.length;
          //dont need to show prospect stage in kanban
          if (stage?.ats_job_milestone?.name === ATS_MILESTONES.LEAD)
            return null;
          const currKandanData = {
            name: stage?.name,
            id: stage?.id,
            ats_job_milestone: stage?.ats_job_milestone?.name,
            mandatory: stage?.mandatory,
            isFirstCallDone: false,
            isLoading: false,
            scrollToken: '',
            ...currAppPerStageWithActions,
            borderColor: borderColors?.[colorIndex],
            offset: 0,
          };

          return currKandanData;
        })
        ?.filter((item) => item);

      state.kanbanData = kanbanData;
    },

    setStageLoading: (state, { payload }) => {
      const { stageId } = payload;
      const kanbanData = [...state.kanbanData];
      const newKanbanData = kanbanData?.reduce((acc, curr) => {
        if (curr?.id === stageId) {
          return [...acc, { ...curr, isLoading: true }];
        } else {
          return [...acc, curr];
        }
      }, []);
      state.kanbanData = newKanbanData;
    },

    setFirstData: (state, { payload }) => {
      const { data, stageId } = payload;
      const { applications, pagination } = data;
      const kanbanData = [...state.kanbanData];
      const newKanbanData = kanbanData?.reduce((acc, curr) => {
        if (curr?.id === stageId) {
          const currAppIds = curr?.applications?.map((app) => app?.id);
          const filteredApplications = applications
            ?.filter((app) => !currAppIds?.includes(app?.id))
            ?.map((app) => ({
              ...app,
              name: `${app?.contact?.first_name || ''} ${
                app?.contact?.last_name || ''
              }`,
            }));
          const newCurr = {
            ...curr,
            applications: filteredApplications?.length
              ? [...(curr?.applications ?? []), ...filteredApplications]
              : [...(curr?.applications ?? [])],
            isFirstCallDone: true,
            isLoading: false,
            offset: pagination?.offset,
          };
          if (filteredApplications?.length) {
            newCurr.scrollToken = pagination?.scroll_token;
          }
          return [...acc, { ...newCurr }];
        } else {
          return [...acc, curr];
        }
      }, []);
      state.kanbanData = newKanbanData;
    },

    setAppReviewSortChangeData: (state, { payload }) => {
      const { data, stageId } = payload;
      const { applications, pagination } = data;
      const kanbanData = [...state.kanbanData];
      const newKanbanData = kanbanData?.reduce((acc, curr) => {
        if (curr?.id === stageId) {
          const filteredApplications = applications?.map((app) => ({
            ...app,
            name: `${app?.contact?.first_name || ''} ${
              app?.contact?.last_name || ''
            }`,
          }));
          const newCurr = {
            ...curr,
            applications: filteredApplications?.length
              ? [...filteredApplications]
              : [],
            isFirstCallDone: true,
            isLoading: false,
            offset: pagination?.offset,
          };
          if (filteredApplications?.length) {
            newCurr.scrollToken = pagination?.scroll_token;
          }
          return [...acc, { ...newCurr }];
        } else {
          return [...acc, curr];
        }
      }, []);
      state.kanbanData = newKanbanData;
    },

    setStageData: (state, { payload }) => {
      const { data, stageId } = payload;
      const { applications, pagination } = data;
      const kanbanData = [...state.kanbanData];
      const newKanbanData = kanbanData?.reduce((acc, curr) => {
        if (curr?.id === stageId) {
          const currAppIds = curr?.applications?.map((app) => app?.id);
          const filteredApplications = applications
            ?.filter((app) => !currAppIds?.includes(app?.id))
            ?.map((app) => ({
              ...app,
              name: `${app?.contact?.first_name || ''} ${
                app?.contact?.last_name || ''
              }`,
            }));
          const newCurr = {
            ...curr,
            applications: filteredApplications?.length
              ? [...(curr?.applications ?? []), ...filteredApplications]
              : [...(curr?.applications ?? [])],
            isLoading: false,
          };
          if (filteredApplications?.length) {
            newCurr.scrollToken = pagination?.scroll_token;
          }
          return [...acc, { ...newCurr }];
        } else {
          return [...acc, curr];
        }
      }, []);
      state.kanbanData = newKanbanData;
    },

    updateKanbanData: (state, { payload }) => {
      const { updatedData } = payload || {};
      state.kanbanData = updatedData;
    },

    updateApplicationData: (state, { payload }) => {
      const { data } = payload;
      const { job_stage_id, id } = data;
      const kanbanData = [...state.kanbanData];
      const newKanbanData = kanbanData?.reduce((acc, curr) => {
        if (curr?.id === job_stage_id) {
          const newCurr = {
            ...curr,
            applications: curr?.applications?.map((app) => {
              if (app?.id === id) {
                // If requisition is explicitly null or undefined in response, ensure it's set to null
                // This handles the case where moving from hired stage removes the requisition
                const updatedApp = { ...app, ...data };
                if (
                  Object.prototype.hasOwnProperty.call(data, 'requisition') &&
                  (data.requisition === null || data.requisition === undefined)
                ) {
                  updatedApp.requisition = null;
                }
                return updatedApp;
              } else return app;
            }),
          };
          return [...acc, { ...newCurr }];
        } else {
          return [...acc, curr];
        }
      }, []);
      state.kanbanData = newKanbanData;
    },

    updateApplicationsCount: (state, { payload }) => {
      const { destination, source } = payload;
      const kanbanData = [...state.kanbanData];
      const newKanbanData = kanbanData?.reduce((acc, curr) => {
        if (curr?.id === destination) {
          return [
            ...acc,
            {
              ...curr,
              applications_count: (curr?.applications_count || 0) + 1,
            },
          ];
        } else if (curr?.id === source) {
          return [
            ...acc,
            { ...curr, applications_count: curr?.applications_count - 1 || 0 },
          ];
        } else {
          return [...acc, curr];
        }
      }, []);
      state.kanbanData = newKanbanData;
    },

    addApplicationsToPipeline: (state, { payload }) => {
      const { data = [], stages = [] } = payload;
      const currAppsStageId = data?.[0]?.job_stage_id;
      const currAppsStage = stages?.find(
        (stage) => stage?.id === currAppsStageId
      );
      const isCurrAppsStageProspect =
        currAppsStage?.name === 'Prospect' &&
        currAppsStage?.mandatory &&
        currAppsStage?.position === 1;
      if (isCurrAppsStageProspect) return;
      const kanbanData = [...state.kanbanData];
      const newApplications = data?.map((application) => ({
        ...application,
        name: `${application?.contact?.first_name || ''} ${
          application?.contact?.last_name || ''
        }`,
      }));
      const newKanbanData = kanbanData?.reduce((acc, curr) => {
        if (curr?.id === currAppsStageId) {
          return [
            ...acc,
            {
              ...curr,
              applications_count:
                (curr?.applications_count || 0) + data?.length,
              applications: [...newApplications, ...(curr?.applications ?? [])],
            },
          ];
        } else {
          return [...acc, curr];
        }
      }, []);
      state.kanbanData = newKanbanData;
    },

    setLoadingState: (state, { payload }) => {
      const { key, isLoading } = payload || {};
      if (state[key]) {
        state[key].isLoading = isLoading;
      }
    },

    setApiData: (state, { payload }) => {
      const { key, data, status, error, isLoading } = payload || {};
      if (state[key]) {
        state[key].data = data;
        state[key].isLoading = isLoading;
        state[key].status = status;
        if (error) {
          state[key].error = error;
        }
      }
    },

    clearAllStageLoadingStates: (state) => {
      const kanbanData = [...state.kanbanData];
      const newKanbanData = kanbanData?.map((stage) => ({
        ...stage,
        isLoading: false,
      }));
      state.kanbanData = newKanbanData;
    },
  },
});

export { pipelineActions };
export default reducer;
